// Mirrors apps/api/modules/evidence/conclusion/boldMarkers.ts: bold in a conclusion paragraph is
// stored as `**` pairs inside the text.

export const BOLD_MARKER = "**";

export function hasBoldMarkers(paragraph: string): boolean {
  return paragraph.includes(BOLD_MARKER);
}

export function stripBoldMarkers(paragraph: string): string {
  return paragraph.split(BOLD_MARKER).join("");
}

export function balancedBoldMarkers(paragraph: string): boolean {
  return paragraph.split(BOLD_MARKER).length % 2 === 1;
}

// The API refuses `****`; a bold toggled on and off by hand leaves exactly that behind.
export function stripEmptyBoldPairs(paragraph: string): string {
  const parts = paragraph.split(BOLD_MARKER);
  let out = parts[0];
  for (let index = 1; index < parts.length; index += 2) {
    const inside = parts[index];
    const after = parts[index + 1];
    if (after === undefined) {
      out += BOLD_MARKER + inside;
      break;
    }
    out += inside === "" ? after : BOLD_MARKER + inside + BOLD_MARKER + after;
  }
  return out;
}

export function toggleBold(text: string, selectionStart: number, selectionEnd: number) {
  if (selectionStart === selectionEnd) return { text, selectionStart, selectionEnd };

  const size = BOLD_MARKER.length;
  // An edge between the two `*` of a marker moves out to the marker's edge, so no marker is cut in half.
  for (let at = text.indexOf(BOLD_MARKER); at !== -1; at = text.indexOf(BOLD_MARKER, at + size)) {
    if (selectionStart === at + 1) selectionStart = at;
    if (selectionEnd === at + 1) selectionEnd = at + size;
  }
  const before = text.slice(0, selectionStart - size);
  // An even count of markers before it makes the one just before the selection an opening marker.
  const insidePair = selectionStart >= size
    && text.slice(selectionStart - size, selectionStart) === BOLD_MARKER
    && text.slice(selectionEnd, selectionEnd + size) === BOLD_MARKER
    && balancedBoldMarkers(before);
  const selected = text.slice(selectionStart, selectionEnd);
  const inner = selected.slice(size, -size);
  const coversPair = selected.length >= 2 * size
    && selected.startsWith(BOLD_MARKER) && selected.endsWith(BOLD_MARKER) && !hasBoldMarkers(inner);
  if (coversPair) {
    return {
      text: text.slice(0, selectionStart) + inner + text.slice(selectionEnd),
      selectionStart,
      selectionEnd: selectionStart + inner.length,
    };
  }
  // Runs inside the selection merge into one; a boundary already inside a run needs no marker of its own.
  if (hasBoldMarkers(selected)) {
    const stripped = stripBoldMarkers(selected);
    const opensInside = !balancedBoldMarkers(text.slice(0, selectionStart));
    const closesInside = !balancedBoldMarkers(text.slice(0, selectionEnd));
    const head = text.slice(0, selectionStart) + (opensInside ? "" : BOLD_MARKER);
    return {
      text: head + stripped + (closesInside ? "" : BOLD_MARKER) + text.slice(selectionEnd),
      selectionStart: head.length,
      selectionEnd: head.length + stripped.length,
    };
  }
  if (insidePair) {
    return {
      text: before + text.slice(selectionStart, selectionEnd) + text.slice(selectionEnd + size),
      selectionStart: selectionStart - size,
      selectionEnd: selectionEnd - size,
    };
  }
  return {
    text: text.slice(0, selectionStart) + BOLD_MARKER + text.slice(selectionStart, selectionEnd) + BOLD_MARKER + text.slice(selectionEnd),
    selectionStart: selectionStart + size,
    selectionEnd: selectionEnd + size,
  };
}
